<template>
  <modal :show="show" :fullscreen="true" @close="close">
    <v-flex slot="title">{{ title }}</v-flex>

    <div>
      <v-layout column>
        <v-flex xs12 px-2>
          <w-schema :model="$schema.name">
            <v-text-field name="name" label="Nazwa usługi*" />
          </w-schema>
        </v-flex>

        <v-flex xs12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.type">
                <v-select label="Rodzaj rabatu*" :items="types" />
              </w-schema>
            </v-flex>

            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.value">
                <v-text-field name="value" :label="valueTitle" />
              </w-schema>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex v-if="shared && !id" xs12 px-2>
          <v-autocomplete
            v-model="selectedPartners"
            clearable
            hide-no-data
            cache-items
            multiple
            chips
            placeholder="Wyszukaj parnera"
            item-text="name"
            item-value="id"
            :items="partnerList"
            :search-input.sync="search"
          >
            <template v-slot:selection="data">
              <v-chip :selected="data.selected" close class="chip--select-multi" @input="removePartner(data.item.id)">
                {{ data.item.name }}
              </v-chip>
            </template>

            <div slot="label">Partnerzy</div>
          </v-autocomplete>
        </v-flex>

        <v-flex xs12 px-2>
          <w-schema :model="$schema.groupIds">
            <v-select
              multiple
              label="Grupy*"
              item-text="name"
              item-value="id"
              :item-disabled="groupDisabledField"
              :items="groups"
              @change="resetIfAllSelected"
            />
          </w-schema>
        </v-flex>

        <v-flex xs12 px-2>
          <w-select
            clearable
            label="Kategoria*"
            item-text="name"
            item-value="id"
            :item-disabled="isCategoryDisabled"
            :model="$schema.categoryId"
            :items="categories"
          />
        </v-flex>

        <v-flex v-if="!needConfirm" xs12 px-2>
          <label>Status *</label>

          <v-radio-group v-model="$schema.state.$model" row>
            <v-radio
              v-for="(state, index) in states"
              :key="'state_' + index"
              :label="state.text"
              :value="state.value"
            />
          </v-radio-group>
        </v-flex>

        <v-flex v-if="!shared" xs12 mx-2>
          <v-radio-group v-model="$schema.placeId.$model">
            <v-radio label="Dostępne we wszystkich obiektach" default :value="null" />

            <v-radio v-for="(items, index) in places" :key="index" :label="items.name" :value="items.id" />
          </v-radio-group>
        </v-flex>

        <v-flex xs12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <datepicker :schema="$schema.start" />
            </v-flex>

            <v-flex xs12 md6 px-2>
              <datepicker :schema="$schema.end" />
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex v-if="hasMaxUsages" md12>
          <v-layout row wrap>
            <v-flex xs12 md4 px-2>
              <w-schema :model="$schema.max">
                <v-text-field type="number" name="value" label="Maksymalna liczba użyć" />
              </w-schema>
            </v-flex>

            <v-flex xs12 md4 px-2>
              <w-schema :model="$schema.maxPerUser">
                <v-text-field type="number" name="value" label="Maksymalna liczba użyć jednego użytkownika" />
              </w-schema>
            </v-flex>

            <v-flex xs12 md4 px-2>
              <w-schema :model="$schema.maxPerUserPeriod">
                <v-select
                  label="Okres*"
                  item-text="text"
                  item-value="value"
                  :disabled="periodsDisabled"
                  :items="periods"
                />
              </w-schema>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex v md12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.waitTimeMinutes">
                <v-text-field type="number" name="value" label="Czas karencji w minutach" />
              </w-schema>
            </v-flex>

            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.waitTimeSeconds">
                <v-text-field type="number" name="value" label="Czas karencji w sekundach" />
              </w-schema>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex md12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.defaultPrice">
                <v-text-field name="default_price" label="Domyślna cena bazowa (bez zniżki)" />
              </w-schema>
            </v-flex>

            <v-flex v-if="defaultValueTitle" xs12 md6 px-2>
              <w-schema :model="$schema.defaultAmount">
                <v-text-field type="number" name="default_amount" :label="defaultValueTitle" />
              </w-schema>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex v-if="show" class="mb-4" px-2>
          <html-editor :schema="$schema.description" />
        </v-flex>

        <v-flex xs12>
          <w-schema :model="$schema.descriptionShort">
            <v-textarea label="Opis skrócony" />
          </w-schema>
        </v-flex>

        <v-flex v-if="show">
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <single-image aspect-ratio="16:9" min-height="300" :schema="$schema.mainImage" />

              <div class="caption">
                * Minimalna wysokość 300 px oraz wymagana proporcja 16:9
              </div>
            </v-flex>

            <v-flex xs12 md6 px-2>
              <images-list min-height="150" min-width="150" :schema="$schema.images" />

              <div class="caption">
                * Minimalna rozdzielczość 150x150 px
              </div>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <span class="caption grey--text">*oznaczone pola są wymagane</span>
    </div>

    <v-layout slot="actions">
      <v-spacer />

      <v-btn color="error" flat @click="close">Anuluj</v-btn>

      <v-btn color="success" flat @click="emailWillBeSent ? confirmAction() : save()">Zapisz</v-btn>
    </v-layout>

    <confirm-modal
      :title="confirmActionModal.title"
      :description="confirmActionModal.description"
      :show="confirmActionModal.show"
      @close="confirmActionModal.show = false"
      @confirm="saveData"
    />
  </modal>
</template>

<script>
import {
  BenefitType,
  DisplayBenefitTypeTranslation,
  getBenefitTypeLabel,
  Period,
  PeriodTranslation,
  PublicSettings,
  State,
  StateTranslation
} from '@witkac/kartamiejska-shared';
import { WSchema, WSelect } from '@witkac/vuetify-components';
import { mapState } from 'vuex';

import HtmlEditor from '@/components/forms/HtmlEditor';
import ImagesList from '@/components/forms/ImagesList';
import SingleImage from '@/components/forms/SingleImage';
import ConfirmModal from '@/components/helpers/ConfirmModal';
import Modal from '@/components/material/Modal';
import { manageMixin } from '@/mixins/manage';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/benefit.schema';
import { tryParseInt } from '@/utils';

const suspendedTypes = [BenefitType.TIME, BenefitType.USAGES];

const emptyData = () => ({
  name: '',
  value: null,
  type: '',
  description: '',
  images: '',
  mainImage: null,
  categoryId: null,
  categoryIds: [],
  groupIds: [],
  placeId: null,
  descriptionShort: '',
  start: '',
  end: '',
  shared: false,
  state: null,
  max: null,
  maxPerUser: null,
  maxPerUserPeriod: null,
  defaultPrice: null,
  defaultAmount: 1,
  waitTime: null,
  waitTimeMinutes: 0,
  waitTimeSeconds: 0
});

const ALL_GROUPS_ID = -1;
const ALL_GROUPS_DISABLE_FIELD = 'disableWhenAll';

// @vuese
// Modal dodawania/edycji usługi
// @group Partners
export default {
  components: { WSchema, WSelect, Modal, ImagesList, SingleImage, HtmlEditor, ConfirmModal },
  mixins: [manageMixin('benefits', {}, emptyData), notificationsMixin],
  schema,
  props: {
    // Czy usługa jest współdzielona
    shared: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      selectedPartners: [],
      search: '',
      showSelect: false,
      types: this.getTypes(),
      confirmActionModal: {
        show: false,
        title: '',
        description: ''
      },
      periods: [
        { text: PeriodTranslation[Period.DAY], value: Period.DAY },
        { text: PeriodTranslation[Period.WEEK], value: Period.WEEK },
        { text: PeriodTranslation[Period.MONTH], value: Period.MONTH },
        { text: PeriodTranslation[Period.YEAR], value: Period.YEAR },
        { text: PeriodTranslation[Period.NONE], value: Period.NONE }
      ]
    };
  },
  watch: {
    show(val) {
      if (val) {
        this.$schema.shared.$setData(this.shared);
        this.$store.dispatch('categories/list', { count: -1 });
        this.$store.dispatch('groups/list', { count: -1 });
      }
    },
    search: {
      immediate: true,
      handler(val) {
        if (this.show) {
          this.searchPartner(val);
        }
      }
    }
  },
  methods: {
    // @vuese
    // Wyszukanie partnerów
    searchPartner(search) {
      this.$store.dispatch('partners/list', { search, count: 5 });
    },
    // @vuese
    // Usunięcie partnera z listy wybranych
    removePartner(id) {
      this.selectedPartners = this.selectedPartners.filter(partnerId => partnerId !== id);
    },
    // @vuese
    // Potwierdzenie akcji
    confirmAction() {
      if (this.id) {
        this.confirmActionModal.title = 'Edytuj usługę';
        this.confirmActionModal.description =
          'Edycja usługi spowoduje wysłanie wiadomości do Administratora Usteckiej Karty Mieszkańca. Czy na pewno chcesz zapisać zmiany w usłudze?';
      } else {
        this.confirmActionModal.title = 'Dodaj usługę';
        this.confirmActionModal.description =
          'Dodanie nowej usługi spowoduje wysłanie wiadomości do Administratora Usteckiej Karty Mieszkańca. Czy na pewno chcesz dodać usługę?';
      }
      this.confirmActionModal.show = true;
    },
    // @vuese
    // Emituje event do zapisu danych
    add() {
      // Emituje event do zapisu danych
      // @arg dane usługi Partnera
      const data = { benefit: this.dataToEmit() };
      if (this.$schema.shared.$model && this.selectedPartners.length) {
        data.partnerIds = this.selectedPartners;
      } else {
        data.partnerId = this.partnerId;
      }
      this.$emit('save', data);
    },
    // @vuese
    // Emituje event do edytowania danych
    update() {
      // Emituje event do edytowania danych
      // @arg dane usługi Partnera
      this.$emit('update', {
        id: this.id,
        benefit: this.dataToEmit()
      });
    },
    // @vuese
    // Zwraca dane benefitu do wyemitowania
    dataToEmit() {
      const data = this.$schema.$getData();
      return { ...data, groupIds: data.groupIds.filter(groupId => groupId !== ALL_GROUPS_ID) };
    },
    // @vuese
    // Zwraca tablicę rodzajów rabatów
    getTypes() {
      return Object.keys(BenefitType)
        .filter(item => {
          const parsedNumber = tryParseInt(item);
          return !isNaN(parsedNumber) && !suspendedTypes.includes(parsedNumber);
        })
        .map(item => ({ text: DisplayBenefitTypeTranslation[BenefitType[item]], value: BenefitType[item] }));
    },
    // @vuese
    // Odznacza pozostałe wartości jeżeli zostanie wybrana grupa "Posiadacze Usteckiej Karty Mieszkańca"
    resetIfAllSelected() {
      if (this.allGroupsSelected) {
        this.$schema.groupIds.$setData([ALL_GROUPS_ID], undefined, true);
      }
    },
    // @vuese
    // Czyszczenie modala potwierdzającego i zapis
    saveData() {
      this.confirmActionModal.show = false;
      this.save();
    },
    // @vuese
    // Sprawdzanie czy kategoria jest nieaktywna
    isCategoryDisabled(category) {
      return category.state !== State[State.ACTIVE];
    },
    // @vuese
    // Nadpisanie metody z mixina
    setData() {
      this.$schema.$setData(this.id ? this.stateData : emptyData.call(this), undefined, true);

      if (this.id) {
        if (this.stateData && suspendedTypes.includes(BenefitType[this.stateData.type])) {
          this.$schema.type.$setData(undefined);
          this.notifyError('Prosimy wybrać nowy typ usługi');
        }
        const availableCategories = this.categories.map(item => item.id);
        if (this.stateData && !this.stateData.categoryIds.every(id => availableCategories.includes(id))) {
          this.notifyError('Prosimy wybrać nową kategorię usługi, inaczej pozostanie ona ukryta');
        }
      }
      this.$schema.$reset();
    }
  },
  computed: {
    ...mapState({
      groups: state => {
        const groups = state.groups.list.map(group => ({ ...group, [ALL_GROUPS_DISABLE_FIELD]: true }));
        return [
          { name: state.settings.settings[PublicSettings.NO_GROUP_SELECTED_NAME].value, id: ALL_GROUPS_ID },
          ...groups
        ];
      },
      partnerList: state => state.partners.list,
      categories(state) {
        return state.categories.list.filter(
          category =>
            category.state === State[State.ACTIVE] ||
            (category.state !== State[State.ACTIVE] &&
              this.id &&
              this.stateData &&
              this.stateData.categoryIds &&
              this.stateData.categoryIds.includes(category.id))
        );
      },
      partnerId: state => state.partners.id,
      stateData(state) {
        const stateData =
          state.benefit.data && state.benefit.data.id === this.id ? state.benefit.data : state.partners.benefits.data;

        if (stateData) {
          if (stateData.groups && stateData.groups.length > 0) {
            stateData.groupIds = stateData.groups.map(group => group.id);
          } else {
            stateData.groupIds = [ALL_GROUPS_ID];
          }

          if (stateData.categories && stateData.categories.length > 0) {
            stateData.categoryIds = stateData.categories.map(category => category.id);
          }
          stateData.placeId = stateData.place && stateData.place.id;
          stateData.waitTime = stateData.waitTime || 0;
          stateData.waitTimeMinutes = Math.floor(stateData.waitTime / 60);
          stateData.waitTimeSeconds = stateData.waitTime - stateData.waitTimeMinutes * 60;
        }
        return stateData;
      },
      places: state => state.partners.places.list,
      needConfirm: state => JSON.parse(state.settings.settings.BENEFIT_CONFIRM.value)
    }),
    states() {
      const states = [
        { text: StateTranslation[State.ACTIVE], value: State[State.ACTIVE] },
        { text: StateTranslation[State.HIDDEN], value: State[State.HIDDEN] }
      ];
      if (this.shared) {
        states.push({ text: StateTranslation[State.INACTIVE], value: State[State.INACTIVE] });
      }
      return states;
    },
    title() {
      return !!this.id ? 'Edycja' : 'Dane usługi';
    },
    hasMaxUsages() {
      const typesWithMaxUsages = [BenefitType.AMOUNT_DISCOUNT, BenefitType.PERCENT_DISCOUNT, BenefitType.FIXED_COST];
      return typesWithMaxUsages.includes(BenefitType[this.$schema.type.$model]);
    },
    valueTitle() {
      return getBenefitTypeLabel(this.$schema.type.$model);
    },
    defaultValueTitle() {
      const type = BenefitType[this.$schema.type.$model];
      if (type === BenefitType.TIME) {
        return 'Domyślna liczba minut';
      } else if (type === BenefitType.USAGES) {
        return 'Domyślna liczba użyć';
      }
    },
    allGroupsSelected() {
      return this.$schema.groupIds.$model.includes(ALL_GROUPS_ID);
    },
    groupDisabledField() {
      if (this.allGroupsSelected) {
        return ALL_GROUPS_DISABLE_FIELD;
      }
    },
    emailWillBeSent() {
      return (
        !this.shared &&
        ((this.id && this.stateData.state !== State[State.HIDDEN]) ||
          (this.id && this.$schema.state.$model !== State[State.HIDDEN]) ||
          !this.id)
      );
    },
    periodsDisabled() {
      return !this.$schema.maxPerUser.$model;
    }
  }
};
</script>

<style>
.v-select__selection--disabled {
  color: red !important;
}
</style>
